import React, { useEffect, useState, useMemo, useRef } from 'react';
import axios from 'axios';
import { ENDPOINTS } from '../../../Utils/Endpoints';
import { configuracionNormalize } from '../../../Objetos/ConfiguracionesModel';
import { Models } from '../../../Utils/ModelsGeneral';
import { normalizeValue } from '../../../../../utils/Texts';
import ConfirmDeleteDialog from '../../../../security/ConfirmDeleteDialog';
import Modal from './ConfiguracionesModal';
import sigmaDelete from '../../../../Pages/img/Borrar.png';
import sigmaEdit from '../../../../Pages/img/Editar.png';
import { useResourcePermissions } from '../../../../../utils/useResourcePermissions';

const PAGE_SIZE = 10;
const nombreTabla = "Configuraciones";
const endpoint = ENDPOINTS.configuraciones;
const endpointById = ENDPOINTS.configuracionById;
const modelo = configuracionNormalize;

const maps = {
    mapCreate: (f) => ({
        idConfiguracion: 0,
        nombre: f.nombre,
        descripcion: f.descripcion,
        valor: f.valor,
        activo: true,
        idEmpresa: f.idEmpresa || null
    }),
    mapUpdate: (id, f) => ({
        idConfiguracion: id || 0,
        nombre: f.nombre,
        descripcion: f.descripcion,
        valor: f.valor,
        activo: f.activo,
        idEmpresa: f.idEmpresa || null
    }),
};

export default function ConfiguracionesCatalog() {
    const { puedeEditar } = useResourcePermissions('Configuracion Del Sistema');

    const [loading, setLoading] = useState(false);
    const [rows, setRows] = useState([]);
    const [query, setQuery] = useState('');
    const [page, setPage] = useState(1);

    const [modalOpen, setModalOpen] = useState(false);
    const [modalMode, setModalMode] = useState('new');
    const [selectedId, setSelectedId] = useState(null);
    const [editData, setEditData] = useState(null);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState({ id: 0, name: '' });

    const [empresas, setEmpresas] = useState([]);
    const [empresasMap, setEmpresasMap] = useState({});

    const editorRef = useRef(null);

    // Cargar datos
    const loadData = async () => {
        setLoading(true);
        try {
            const res = await axios.get(endpoint);
            const data = (res.data || []).map((x) => modelo(x));
            setRows(data);

            // Cargar empresas para el mapeo visual
            const resEmp = await axios.get(ENDPOINTS.empresas);
            const normalizedEmpresas = (resEmp.data || []).map(emp => Models.empresas(emp));
            setEmpresas(normalizedEmpresas);
            const mapE = {};
            normalizedEmpresas.forEach(emp => {
                mapE[emp.idEmpresa] = emp.nombre;
            });
            setEmpresasMap(mapE);
        } catch (error) {
            console.error('Error al cargar configuraciones:', error);
            if (!rows.length) setRows([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadData();
    }, []);

    useEffect(() => {
        if (modalOpen && editorRef.current) {
            editorRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [modalOpen, selectedId]);

    // Filtrado
    const filtered = useMemo(() => {
        let data = rows;
        if (query.trim()) {
            const q = normalizeValue(query);
            data = data.filter((e) => {
                const nom = normalizeValue(e.nombre);
                const desc = normalizeValue(e.descripcion);
                return (
                    nom.includes(q) ||
                    desc.includes(q)
                );
            });
        }
        return data;
    }, [rows, query]);

    // Paginación
    const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
    const paginated = useMemo(() => {
        const start = (page - 1) * PAGE_SIZE;
        return filtered.slice(start, start + PAGE_SIZE);
    }, [filtered, page]);

    useEffect(() => { if (page > totalPages) setPage(1); }, [totalPages, page]);

    const PageBtn = ({ label, onClick, active, disabled }) => (
        <button
            type="button"
            className={`roles-page-btn ${active ? 'is-active' : ''} ${disabled ? 'is-disabled' : ''}`}
            onClick={onClick}
            disabled={disabled}
        >
            {label}
        </button>
    );

    const pageNumbers = (() => {
        const maxToShow = 5;
        let start = Math.max(1, page - 2);
        let end = Math.min(totalPages, start + maxToShow - 1);
        start = Math.max(1, end - maxToShow + 1);
        return Array.from({ length: end - start + 1 }, (_, i) => start + i);
    })();

    const startIndex = filtered.length === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
    const endIndex = Math.min(page * PAGE_SIZE, filtered.length);

    // Nuevo
    const handleNew = () => {
        setSelectedId(null);
        setEditData(null);
        setModalMode('new');
        setModalOpen(true);
    };

    // Editar
    const handleEdit = (id) => {
        const r = rows.find((x) => x.idConfiguracion === id);
        if (!r) return;
        setSelectedId(id);
        setEditData(r);
        setModalMode('edit');
        setModalOpen(true);
    };

    // Borrar
    const askDelete = (id, name) => {
        setDeleteTarget({ id, name });
        setDeleteOpen(true);
    };

    const doDelete = async () => {
        try {
            setLoading(true);
            await axios.delete(endpointById(deleteTarget.id));
            setDeleteOpen(false);
            await loadData();
        } catch (e) {
            console.error(e);
            alert('Error al eliminar');
        } finally {
            setLoading(false);
        }
    };

    // Guardar
    const handleSubmit = async (form, mode) => {
        try {
            setLoading(true);
            if (mode === 'new') {
                await axios.post(endpoint, maps.mapCreate(form));
            } else {
                await axios.put(endpoint, maps.mapUpdate(selectedId, form));
            }
            setModalOpen(false);
            await loadData();
        } catch (e) {
            console.error(e);
            alert('Error al guardar');
        } finally {
            setLoading(false);
        }
    };

    const handleToggleValor = async (config) => {
        const valStr = (config.valor || '').toString().trim();
        const nuevoValor = valStr === '1' ? '0' : '1';
        const payload = {
            idConfiguracion: config.idConfiguracion,
            nombre: config.nombre,
            descripcion: config.descripcion,
            valor: nuevoValor,
            activo: config.activo,
            idEmpresa: config.idEmpresa
        };

        try {
            setLoading(true);
            await axios.put(endpoint, payload);
            await loadData();
        } catch (e) {
            console.error('Error al cambiar valor:', e);
            alert('Error al cambiar valor');
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <div className="panel-sigma-main">
                <div className="roles-shell">
                    <div className="roles-topbar">
                        <h2 className="roles-title">{nombreTabla}</h2>

                        <input
                            className="roles-search"
                            placeholder="Buscar..."
                            value={query}
                            onChange={(e) => {
                                setQuery(e.target.value);
                                setPage(1);
                            }}
                        />

                        {/* 
                        <button
                            type="button"
                            className="btn-action"
                            onClick={handleNew}
                        >
                            ＋ Nuevo
                        </button>
                        */}
                    </div>

                    {loading ? (
                        <div className="catalog-loading">Cargando...</div>
                    ) : (
                        <div className="roles-table-wrap">
                            <div className="roles-table-scroller">
                                <table className="roles-table">
                                    <thead>
                                        <tr>
                                            <th>Nombre</th>
                                            <th style={{ textAlign: 'center' }}>Valor</th>
                                            <th>Descripción</th>
                                            <th>Empresa</th>
                                            {/* <th style={{ textAlign: 'center' }}>Editar</th>
                                            <th style={{ textAlign: 'center' }}>Borrar</th> */}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {paginated.length === 0 ? (
                                            <tr>
                                                <td colSpan="4" style={{ textAlign: 'center' }}>
                                                    No se encontraron registros.
                                                </td>
                                            </tr>
                                        ) : (
                                            paginated.map((e) => (
                                                <tr key={e.idConfiguracion}>
                                                    <td>{e.nombre}</td>
                                                    <td style={{ verticalAlign: 'middle', textAlign: 'center' }}>
                                                        {((e.valor || '').toString().trim() === '1' || (e.valor || '').toString().trim() === '0') ? (
                                                            puedeEditar ? (
                                                                <div
                                                                    onClick={() => !loading && handleToggleValor(e)}
                                                                    style={{
                                                                        width: '38px',
                                                                        height: '20px',
                                                                        backgroundColor: (e.valor || '').toString().trim() === '1' ? '#10b981' : '#cbd5e1',
                                                                        borderRadius: '12px',
                                                                        position: 'relative',
                                                                        cursor: loading ? 'not-allowed' : 'pointer',
                                                                        transition: 'background-color 0.2s',
                                                                        flexShrink: 0,
                                                                        opacity: loading ? 0.6 : 1,
                                                                        margin: '0 auto'
                                                                    }}
                                                                    title={(e.valor || '').toString().trim() === '1' ? "Desactivar" : "Activar"}
                                                                >
                                                                    <div style={{
                                                                        width: '14px',
                                                                        height: '14px',
                                                                        backgroundColor: 'white',
                                                                        borderRadius: '50%',
                                                                        position: 'absolute',
                                                                        top: '3px',
                                                                        left: (e.valor || '').toString().trim() === '1' ? '21px' : '3px',
                                                                        transition: 'left 0.2s',
                                                                        boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
                                                                    }} />
                                                                </div>
                                                            ) : (
                                                                <span style={{ fontSize: '0.86rem', fontWeight: 600, color: (e.valor || '').toString().trim() === '1' ? '#10b981' : '#94a3b8' }}>
                                                                    {(e.valor || '').toString().trim() === '1' ? 'Sí' : 'No'}
                                                                </span>
                                                            )
                                                        ) : (
                                                            <span style={{ fontSize: '0.86rem', color: '#475569', fontWeight: 500 }}>{e.valor}</span>
                                                        )}
                                                    </td>
                                                    <td>{e.descripcion}</td>
                                                    <td>
                                                        <span style={{ fontSize: '0.85rem', color: e.idEmpresa ? '#334155' : '#94a3b8', fontWeight: e.idEmpresa ? 600 : 400 }}>
                                                            {e.idEmpresa ? (empresasMap[e.idEmpresa] || `ID: ${e.idEmpresa}`) : 'Global'}
                                                        </span>
                                                    </td>
                                                    {/*
                                                    <td style={{ textAlign: 'center' }}>
                                                        <button
                                                            type="button"
                                                            className="rk-iconbtn rk-iconbtn--edit"
                                                            onClick={() => handleEdit(e.idConfiguracion)}
                                                            title="Editar"
                                                        >
                                                            <img src={sigmaEdit} alt="" className="rk-ico" />
                                                        </button>
                                                    </td>
                                                    <td style={{ textAlign: 'center' }}>
                                                        <button
                                                            type="button"
                                                            className="rk-iconbtn rk-iconbtn--del"
                                                            onClick={() => askDelete(e.idConfiguracion, e.nombre)}
                                                            title="Borrar"
                                                        >
                                                            <img src={sigmaDelete} alt="" className="rk-ico" />
                                                        </button>
                                                    </td>
                                                    */}
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    <div className="roles-pagination">
                        <div className="roles-pagination-left">
                            {filtered.length > 0
                                ? <span>Mostrando {startIndex}–{endIndex} de {filtered.length}</span>
                                : <span>0 resultados</span>}
                        </div>
                        <div className="roles-pagination-right">
                            <PageBtn
                                label="‹"
                                onClick={() => setPage((p) => Math.max(1, p - 1))}
                                disabled={page === 1}
                            />
                            {pageNumbers.map((n) => (
                                <PageBtn
                                    key={n}
                                    label={n}
                                    onClick={() => setPage(n)}
                                    active={n === page}
                                />
                            ))}
                            <PageBtn
                                label="›"
                                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                                disabled={page === totalPages}
                            />
                        </div>
                    </div>

                    {modalOpen && (
                        <div className="roles-editor-slot" ref={editorRef}>
                            <Modal
                                open={modalOpen}
                                mode={modalMode}
                                data={editData}
                                onClose={() => setModalOpen(false)}
                                onSubmit={handleSubmit}
                                empresas={empresas}
                            />
                        </div>
                    )}
                </div>
            </div>

            <ConfirmDeleteDialog
                open={deleteOpen}
                roleName={deleteTarget.name}
                onClose={() => setDeleteOpen(false)}
                onConfirm={doDelete}
            />
        </>
    );
}
